import { useEffect, useState } from "react";
import api from "@/lib/api";
import { buildParams, fmtVal, recordTitle, optVal, optLabel, LONG_TYPES } from "@/lib/records";
import { fmtDate } from "@/lib/fmt";

const PER = 20;

function Column({ mkey, field, value, label, count, base, fields, onOpen, titleField, cardFields }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = (skip) => {
    setBusy(true);
    const p = buildParams({ ...base, filters: { ...base.filters, [field]: value == null || value === "" ? "__empty__" : value }, limit: PER, skip });
    api.get(`/crm/modules/${mkey}/records?${p}`).then((r) => setRows((x) => (skip ? [...x, ...r.data.rows] : r.data.rows))).finally(() => setBusy(false));
  };
  useEffect(() => { load(0); }, [JSON.stringify(base), value]); // eslint-disable-line react-hooks/exhaustive-deps
  const peek = cardFields?.length ? fields.filter((f) => cardFields.includes(f.key)) : fields.filter((f) => f.key !== field && f.key !== titleField && !LONG_TYPES.includes(f.type)).slice(1, 3);
  return (
    <div className="w-[280px] shrink-0 flex flex-col max-h-[72vh]" data-testid={`board-col-${label}`}>
      <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium">
        <span className="truncate">{label}</span>
        <span className="ml-auto text-[var(--text-dim)] tabular-nums" data-testid={`board-count-${label}`}>{count}</span>
      </div>
      <div className="flex-1 overflow-y-auto space-y-2 p-1">
        {rows.map((r) => (
          <button key={r._id} onClick={() => onOpen(r)} className="w-full text-left panel p-2.5 hover:border-[var(--border-strong)]" data-testid={`board-card-${r._id}`}>
            <div className="text-sm line-clamp-2 break-words">{recordTitle(r, fields, titleField)}</div>
            {peek.map((f) => r.data?.[f.key] != null && r.data[f.key] !== "" && (
              <div key={f.key} className="text-[11px] text-[var(--text-dim)] truncate">{f.label}: {fmtVal(r.data[f.key])}</div>
            ))}
            <div className="text-[10px] text-[var(--text-faint)] mt-1">{fmtDate(r.updated_at)}</div>
          </button>
        ))}
        {rows.length < count && (
          <button className="btn text-xs w-full justify-center" disabled={busy} onClick={() => load(rows.length)} data-testid={`board-more-${label}`}>{busy ? "Loading…" : `Load more (${count - rows.length})`}</button>
        )}
      </div>
    </div>
  );
}

export default function RecordsBoard({ mkey, field, fields, groups, base, onOpen, titleField, cardFields, reload }) {
  const def = fields.find((f) => f.key === field);
  const opts = (def?.options || []).map((o) => ({ v: optVal(o), l: optLabel(o) }));
  const seen = new Set(opts.map((o) => o.v));
  const cols = [...opts, ...groups.filter((g) => !seen.has(g.value)).map((g) => ({ v: g.value, l: g.value == null || g.value === "" ? "(empty)" : String(g.value) }))];
  const count = (v) => groups.find((g) => g.value === v)?.count || 0;
  if (!field) return <div className="panel p-6 text-sm text-[var(--text-dim)]">This module has no board field set.</div>;
  return (
    <div className="flex gap-3 overflow-x-auto pb-3" data-testid="records-board">
      {cols.filter((c) => !(c.v == null && !count(c.v))).map((c) => (
        <Column key={`${String(c.v)}-${reload}`} mkey={mkey} field={field} value={c.v} label={c.l} count={count(c.v)} base={base} fields={fields} onOpen={onOpen} titleField={titleField} cardFields={cardFields} />
      ))}
    </div>
  );
}
